import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, Building2 } from 'lucide-react';
import apiClient from '../services/api';

const NAVY       = '#0d1c6b';
const NAVY_LIGHT = '#1a3a8f';

const Departments = () => {
  const getSessionData = () => {
    try {
      const s = localStorage.getItem('ltms_session');
      if (!s) return null;
      const d = JSON.parse(s);
      return d?.session || d;
    } catch { return null; }
  };

  const session   = getSessionData();
  const companyId = session?.companyid;
  const yearId    = session?.yearid;

  const [departments,         setDepartments]         = useState([]);
  const [filteredDepartments, setFilteredDepartments] = useState([]);
  const [employees,           setEmployees]           = useState([]);
  const [loading,             setLoading]             = useState(true);
  const [searchTerm,          setSearchTerm]          = useState('');
  const [showModal,           setShowModal]           = useState(false);
  const [editMode,            setEditMode]            = useState(false);
  const [submitLoading,       setSubmitLoading]       = useState(false);
  const [errors,              setErrors]              = useState({});

  const [formData, setFormData] = useState({
    departmentid: null, companyid: companyId || null,
    departmentcode: '', departmentname: '',
    parentdepartmentid: '', departmenthead: '', isactive: true,
  });

  useEffect(() => {
    if (!companyId || !yearId) { setLoading(false); return; }
    fetchEmployees(); fetchDepartments();
  }, [companyId, yearId]);

  useEffect(() => { filterDepartments(); }, [searchTerm, departments]);

  const fetchEmployees = async () => {
    try {
      const res = await apiClient.get('/Employees.php', { params: { isactive: 1, companyid: companyId } });
      if (res.data.success) setEmployees(res.data.data || []);
    } catch (err) {
      if (err.response?.status === 401) alert('Session expired. Please login again.');
    }
  };

  const fetchDepartments = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/Departments.php', { params: { companyid: companyId } });
      if (res.data.success) setDepartments(res.data.data || []);
      else alert('Failed to fetch departments: ' + res.data.message);
    } catch (err) {
      if (err.response?.status === 401) alert('Session expired. Please login again.');
      else alert('Error fetching departments. Please check if backend is running.');
    } finally { setLoading(false); }
  };

  const filterDepartments = () => {
    if (!searchTerm.trim()) { setFilteredDepartments(departments); return; }
    const q = searchTerm.toLowerCase();
    setFilteredDepartments(departments.filter(d =>
      d.departmentname.toLowerCase().includes(q) ||
      d.departmentcode?.toLowerCase().includes(q)
    ));
  };

  const validateForm = () => {
    const errs = {};
    if (!formData.departmentname.trim()) errs.departmentname = 'Department name is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitLoading(true);
    try {
      const method = editMode ? 'put' : 'post';
      const res = await apiClient[method]('/Departments.php', {
        ...formData, companyid: companyId, isactive: formData.isactive ? 1 : 0,
      });
      if (res.data.success) {
        alert(!editMode && res.data.data?.departmentcode
          ? `Department created successfully!\nDepartment Code: ${res.data.data.departmentcode}`
          : editMode ? 'Department updated successfully!' : 'Department created successfully!');
        closeModal(); fetchDepartments();
      } else { alert('Error: ' + res.data.message); }
    } catch (err) {
      if (err.response?.status === 401) alert('Session expired. Please login again.');
      else alert('Error saving department. Please try again.');
    } finally { setSubmitLoading(false); }
  };

  const handleEdit = (dept) => {
    setFormData({
      departmentid: dept.departmentid, companyid: dept.companyid,
      departmentcode: dept.departmentcode, departmentname: dept.departmentname,
      parentdepartmentid: dept.parentdepartmentid || '',
      departmenthead: dept.departmenthead || '', isactive: dept.isactive === 1,
    });
    setEditMode(true); setShowModal(true); setErrors({});
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this department?')) return;
    try {
      const res = await apiClient.delete('/Departments.php', { data: { departmentid: id } });
      if (res.data.success) { alert('Department deleted successfully!'); fetchDepartments(); }
      else alert('Error: ' + res.data.message);
    } catch (err) {
      if (err.response?.status === 401) alert('Session expired. Please login again.');
      else alert('Error deleting department. Please try again.');
    }
  };

  const resetForm    = () => { setFormData({ departmentid: null, companyid: companyId || null, departmentcode: '', departmentname: '', parentdepartmentid: '', departmenthead: '', isactive: true }); setEditMode(false); setErrors({}); };
  const openAddModal = () => { resetForm(); setShowModal(true); };
  const closeModal   = () => { setShowModal(false); resetForm(); };

  const inputCls  = (err) => `w-full px-3.5 py-3 border-2 rounded-lg text-sm text-gray-800 font-medium bg-white outline-none transition-all duration-200 focus:border-[#0d1c6b] focus:shadow-[0_0_0_4px_rgba(13,28,107,0.1)] ${err ? 'border-red-500 bg-red-50' : 'border-gray-200'}`;
  const selectCls = `w-full px-3.5 py-3 border-2 border-gray-200 rounded-lg text-sm text-gray-800 font-medium bg-white outline-none transition-all duration-200 focus:border-[#0d1c6b] focus:shadow-[0_0_0_4px_rgba(13,28,107,0.1)]`;

  // ── no session ─────────────────────────────────────────────────────────
  if (!companyId || !yearId) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center p-10 bg-white rounded-xl shadow-sm border border-gray-200">
          <Building2 size={40} className="mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-bold text-gray-700 mb-2">No Session Context</h3>
          <p className="text-gray-500 text-sm">Please select a company and financial year from the dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-gray-50 min-h-screen">

      {/* ── HEADER ── */}
      <div className="flex items-center justify-between px-6 py-4 flex-shrink-0"
        style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
            <Building2 size={22} color="#fff" />
          </div>
          <div>
            <h1 className="text-xl font-bold leading-tight" style={{ color: '#fff' }}>Departments Master</h1>
            <p className="text-[13px]" style={{ color: 'rgba(255,255,255,0.8)' }}>Manage organizational departments</p>
          </div>
        </div>
        <button onClick={openAddModal}
          className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-lg border-0 text-sm font-bold cursor-pointer transition-all duration-150 hover:bg-blue-50"
          style={{ color: NAVY }}>
          <Plus size={17} /> Add Department
        </button>
      </div>

      {/* ── TOOLBAR ── */}
      <div className="flex items-center gap-3 flex-wrap px-5 py-3 bg-white border-b border-gray-200 flex-shrink-0">
        <div className="flex-1 min-w-[220px] flex items-center gap-2 bg-gray-50 border-2 border-gray-200 rounded-lg px-3
          focus-within:border-[#0d1c6b] focus-within:bg-white focus-within:shadow-[0_0_0_4px_rgba(13,28,107,0.1)] transition-all duration-200">
          <Search size={16} className="text-gray-400 flex-shrink-0" />
          <input type="text" placeholder="Search by department name or code..."
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="flex-1 border-0 outline-none py-2.5 text-sm text-gray-800 bg-transparent font-medium placeholder:text-gray-400 placeholder:font-normal" />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')}
              className="p-1 rounded cursor-pointer bg-transparent border-0 text-gray-400 hover:text-red-500 hover:bg-gray-100">
              <X size={14} />
            </button>
          )}
        </div>
        <div className="text-sm font-semibold text-gray-500 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg whitespace-nowrap">
          Total: {filteredDepartments.length} {filteredDepartments.length === 1 ? 'department' : 'departments'}
        </div>
      </div>

      {/* ── BODY ── */}
      <div className="flex-1 p-5">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400 text-lg font-semibold">
            Loading departments...
          </div>
        ) : filteredDepartments.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-gray-200">
            <Building2 size={40} className="text-gray-300 mb-4" />
            <p className="text-xl text-gray-400 font-semibold mb-3">No departments found</p>
            {searchTerm && (
              <button onClick={() => setSearchTerm('')}
                className="text-sm text-blue-600 underline cursor-pointer bg-transparent border-0">
                Clear search
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto bg-white rounded-xl border border-gray-200 shadow-sm">
            <table className="w-full border-collapse">
              <thead>
                <tr style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
                  {['Code', 'Department Name', 'Parent Department', 'Department Head', 'Status', 'Created At', 'Actions'].map(h => (
                    <th key={h} className="px-4 py-3.5 text-left text-[11px] font-bold uppercase tracking-wider whitespace-nowrap sticky top-0"
                      style={{ color: '#fff', background: 'inherit' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredDepartments.map(dept => (
                  <tr key={dept.departmentid}
                    className="border-b border-gray-100 transition-all duration-150 hover:bg-gray-50 hover:translate-x-0.5 hover:shadow-[-2px_0_0_#0d1c6b]">
                    <td className="px-4 py-3.5">
                      <span className="font-mono text-[12px] font-bold text-violet-700 bg-violet-50 px-2 py-0.5 rounded">
                        {dept.departmentcode}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2">
                        <Building2 size={15} className="text-[#667eea] flex-shrink-0" />
                        <span className="font-semibold text-gray-800">{dept.departmentname}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-gray-600 text-sm">{dept.parentdepartmentname || '-'}</td>
                    <td className="px-4 py-3.5 text-gray-600 text-sm">{dept.departmentheadname || '-'}</td>
                    <td className="px-4 py-3.5">
                      <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wide border-2 ${
                        dept.isactive
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-400'
                          : 'bg-red-50 text-red-700 border-red-400'
                      }`}>
                        {dept.isactive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-sm text-gray-500">
                      {new Date(dept.createdat).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex gap-1.5">
                        <button onClick={() => handleEdit(dept)} title="Edit"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer bg-violet-100 border-violet-400 text-violet-700 transition-all duration-150 hover:bg-violet-700 hover:text-white hover:scale-110 hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(109,40,217,0.3)]">
                          <Edit2 size={13} />
                        </button>
                        <button onClick={() => handleDelete(dept.departmentid)} title="Delete"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer bg-red-100 border-red-400 text-red-600 transition-all duration-150 hover:bg-red-600 hover:text-white hover:scale-110 hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(220,38,38,0.3)]">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── MODAL ── */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-5"
          style={{ animation: 'fadeIn 0.2s ease' }}>
          <div className="bg-white rounded-2xl w-full max-w-[520px] shadow-2xl border border-gray-200 overflow-hidden"
            style={{ animation: 'slideUp 0.35s cubic-bezier(0.16,1,0.3,1)' }}
            onClick={e => e.stopPropagation()}>

            {/* Modal header */}
            <div className="flex items-center justify-between px-7 py-5"
              style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
              <h3 className="flex items-center gap-2 text-[18px] font-bold uppercase tracking-wide m-0" style={{ color: '#fff' }}>
                <Building2 size={18} />
                {editMode ? 'Edit Department' : 'Add New Department'}
              </h3>
              <button onClick={closeModal}
                className="w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer border-0 bg-white/20 text-white transition-all duration-200 hover:bg-white/30 hover:rotate-90"
                style={{ color: '#fff' }}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-7">

              {/* Auto-code banner */}
              {!editMode && (
                <div className="flex items-center gap-2.5 px-4 py-3 mb-5 rounded-lg text-[13.5px] font-semibold text-indigo-800
                  bg-gradient-to-r from-blue-50 to-violet-50 border border-indigo-200">
                  <span>ℹ️</span>
                  <span>Department code will be auto-generated (e.g., DEPT001, DEPT002, …)</span>
                </div>
              )}

              {/* Code display (edit mode) */}
              {editMode && formData.departmentcode && (
                <div className="mb-4">
                  <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">
                    Department Code (Auto-generated)
                  </label>
                  <input type="text" value={formData.departmentcode} disabled
                    className="w-full px-3.5 py-3 border-2 border-gray-200 rounded-lg text-sm text-gray-500 bg-gray-100 cursor-not-allowed" />
                </div>
              )}

              {/* Department Name */}
              <div className="mb-4">
                <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">
                  Department Name <span className="text-red-500">*</span>
                </label>
                <input type="text" value={formData.departmentname}
                  onChange={e => setFormData({ ...formData, departmentname: e.target.value })}
                  maxLength="100" placeholder="e.g., Human Resources, Sales, IT"
                  className={inputCls(errors.departmentname)} />
                {errors.departmentname && <p className="text-xs text-red-600 font-semibold mt-1">⚠ {errors.departmentname}</p>}
              </div>

              {/* Parent Department */}
              <div className="mb-1.5">
                <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">Parent Department</label>
                <select value={formData.parentdepartmentid}
                  onChange={e => setFormData({ ...formData, parentdepartmentid: e.target.value })}
                  className={selectCls}>
                  <option value="">-- None (Top Level) --</option>
                  {departments.filter(d => d.departmentid !== formData.departmentid).map(d => (
                    <option key={d.departmentid} value={d.departmentid}>
                      {d.departmentname} ({d.departmentcode})
                    </option>
                  ))}
                </select>
                <small className="text-[11px] text-gray-400 mt-1 block">Optional: Select if this is a sub-department</small>
              </div>

              {/* Department Head */}
              <div className="mb-4">
                <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">Department Head</label>
                <select value={formData.departmenthead}
                  onChange={e => setFormData({ ...formData, departmenthead: e.target.value })}
                  className={selectCls}>
                  <option value="">-- Select Employee --</option>
                  {employees.map(emp => (
                    <option key={emp.employeeid} value={emp.employeeid}>
                      {emp.employeename} ({emp.employeecode})
                    </option>
                  ))}
                </select>
                <small className="text-[11px] text-gray-400 mt-1 block">Optional: Assign a manager/head for this department</small>
              </div>

              {/* Active */}
              <div className="mb-6">
                <label className="flex items-center gap-3 cursor-pointer px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-lg hover:bg-gray-100 hover:border-gray-300 transition-all duration-150">
                  <input type="checkbox" checked={formData.isactive}
                    onChange={e => setFormData({ ...formData, isactive: e.target.checked })}
                    className="w-5 h-5 cursor-pointer accent-[#0d1c6b]" />
                  <span className="text-sm font-semibold text-gray-700">Active</span>
                </label>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 pt-5 border-t border-gray-200">
                <button type="button" onClick={closeModal} disabled={submitLoading}
                  className="px-5 py-2.5 text-sm font-bold text-gray-700 bg-white border-2 border-gray-200 rounded-lg cursor-pointer transition-all duration-200 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-60 disabled:cursor-not-allowed">
                  Cancel
                </button>
                <button type="submit" disabled={submitLoading}
                  className="px-5 py-2.5 text-sm font-bold border-0 rounded-lg cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_6px_16px_rgba(13,28,107,0.35)] disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                  style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)`, color: '#fff' }}>
                  {submitLoading ? 'Saving...' : (editMode ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn  { from { opacity:0 } to { opacity:1 } }
        @keyframes slideUp { from { transform:translateY(40px) scale(0.96);opacity:0 } to { transform:translateY(0) scale(1);opacity:1 } }
      `}</style>
    </div>
  );
};

export default Departments;